import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, ArrowUpRight } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import {
  formatMoney,
  formatPlanPrice,
  formatProductsPhrase,
  isCapTier,
  lookupForCycle,
  planDisplayName,
  planPrice,
  type PlanInfo,
} from '@/lib/plans';

/**
 * Was der Knopf einer Karte tut (UX-Audit K37/K38):
 *   - checkout:   Neukunde bucht das Band.
 *   - bandChange: Abonnent wechselt das Band (Bestaetigung in der Seite).
 *   - none:       Sondertarif, die Karte ist nur Information.
 */
export type PlanCardCta = 'checkout' | 'bandChange' | 'none';

interface PlanCardProps {
  plan: PlanInfo;
  billingCycle: 'monthly' | 'yearly';
  currency: 'eur' | 'usd';
  isCurrent: boolean;
  isRecommended: boolean;
  cta: PlanCardCta;
  onUpgrade: (lookupKey: string) => void;
  loading: boolean;
  /** Sperrt den Knopf, z. B. solange das Abo laedt oder ein Kauf laeuft. */
  disabled?: boolean;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  billingCycle,
  currency,
  isCurrent,
  isRecommended,
  cta,
  onUpgrade,
  loading,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation('pricing');
  const language = i18n?.language;
  const isBand = isCapTier(plan.tier);
  const isYearly = billingCycle === 'yearly' && plan.tier !== 'free';

  // Zyklus-Semantik (siehe lib/plans.ts): Baender speichern den vollen
  // JAHRES-Betrag, die Legacy-Tarife den Pro-Monat-Aequivalenzpreis.
  const yearlyNum = planPrice(plan, currency, 'yearly');
  const monthlyNum = planPrice(plan, currency, 'monthly');
  const fullYearUndiscounted = monthlyNum * 12;
  const discountPercent = isYearly
    ? isBand
      ? Math.round((1 - yearlyNum / fullYearUndiscounted) * 100)
      : Math.round((1 - yearlyNum / monthlyNum) * 100)
    : 0;

  // UX-Audit A7O-05: Preise im Format der App-Sprache.
  const price = formatPlanPrice(plan, currency, isYearly ? 'yearly' : 'monthly', language);
  const strikethrough = isBand
    ? formatMoney(fullYearUndiscounted, currency, language)
    : formatPlanPrice(plan, currency, 'monthly', language);
  const priceSuffix = isYearly && isBand ? t('perYear') : t('perMonth');

  const lookupKey = lookupForCycle(plan, billingCycle);
  const displayName = planDisplayName(plan, language, t);

  // C-24: kurze Zielgruppen-Orientierung je Tarif. i18next gibt bei einem
  // fehlenden Key den Key selbst zurueck, darauf pruefen wir: ein Tarif ohne
  // eigene Tagline zeigt keine Zeile, nie den rohen Schluessel.
  const taglineKey = `taglines.${plan.tier}`;
  const tagline = t(taglineKey);
  const hasTagline = tagline !== taglineKey;

  // UX-Audit A7O-07: kurzer Knopftext (der Bandname steht schon im Titel und
  // im zugaenglichen Namen) plus Umbruch statt Ueberlauf.
  const ctaLabel = cta === 'bandChange' ? t('cta.switch') : t('cta.checkout');

  return (
    <Card className={`relative flex flex-col ${
      isRecommended
        ? 'border-primary shadow-lg ring-1 ring-primary/20'
        : isCurrent
        ? 'border-primary/50 bg-primary/5'
        : ''
    }`}>
      {isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary text-primary-foreground shadow-sm">
            {t('status.recommended', { ns: 'common' })}
          </Badge>
        </div>
      )}
      {isCurrent && !isRecommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge variant="secondary">{t('currentPlan')}</Badge>
        </div>
      )}

      <CardHeader className="pb-4 pt-6">
        <CardTitle className="text-lg">{displayName}</CardTitle>
        {/* `div`, bewusst kein `p`: productsPhrase.test.tsx findet die
            Kapazitaetszeile ueber `container.querySelector('p')`. */}
        {hasTagline && (
          <div className="text-xs text-muted-foreground">{tagline}</div>
        )}
        <div className="mt-2">
          {isYearly && (
            <span className="text-sm text-muted-foreground line-through mr-2">
              {strikethrough}
            </span>
          )}
          <span className="text-3xl font-bold">{price}</span>
          {plan.tier !== 'free' && (
            <span className="text-sm text-muted-foreground">{priceSuffix}</span>
          )}
        </div>
        {isYearly && discountPercent > 0 ? (
          <Badge className="mt-1 w-fit text-xs bg-success-light text-success-ink border-success/30 hover:bg-success-light">
            {t('savePercent', { percent: discountPercent })}
          </Badge>
        ) : (
          <div className="h-5 mt-1" />
        )}
        {/* Baender heissen bereits wie ihre Kapazitaet, die Zeile wuerde den
            Titel woertlich wiederholen. Free und Legacy behalten sie. */}
        {!isBand && (
          <p className="text-sm text-muted-foreground mt-1">
            {formatProductsPhrase(plan, language, t)}
          </p>
        )}
      </CardHeader>

      <CardContent className="flex-1 flex flex-col">
        <ul className="space-y-2 flex-1 mb-6">
          {plan.features.map((featureKey) => (
            <li key={featureKey} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-primary flex-shrink-0" />
              {t(`features.${featureKey}`)}
            </li>
          ))}
        </ul>

        {isCurrent ? (
          <Button variant="outline" className="w-full" disabled>
            {t('currentPlan')}
          </Button>
        ) : plan.tier === 'free' ? (
          <Button variant="outline" className="w-full" disabled>
            {t('free')}
          </Button>
        ) : cta === 'none' ? null : (
          <Button
            className="w-full gap-2 h-auto min-h-10 whitespace-normal"
            variant={isRecommended ? 'gradient' : 'outline'}
            onClick={() => onUpgrade(lookupKey)}
            disabled={loading || disabled}
            aria-label={t('cta.aria', { cta: ctaLabel, plan: displayName })}
          >
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            )}
            {ctaLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
